import {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useQuery} from '@apollo/client';
import {Dropdown, Input, Pagination, Table, Tooltip} from 'antd';
import {Button} from '@dabafinance/react-components';
import {SearchOutlined} from '@ant-design/icons';
import {debounce, sanitizeInput} from 'utils/helper';
import shell from 'pages/AssetManagement/v2/pageShell.module.scss';
import v2 from 'pages/AssetManagement/v2/instrumentV2.module.scss';
import {GET_ASSET_CLASS_INSTRUMENTS} from 'graphql/queries/assetManagement';
import {ExchangeScopeSelector} from 'pages/exchanges/ExchangeScopeSelector';
import {
  INSTRUMENT_TYPES,
  useExchangeScope,
} from 'pages/exchanges/useExchangeScope';
import {instrumentKeysFor} from 'pages/AssetManagement/modules/instrumentKeys';
import {
  inventoryColumns,
  inventoryScrollWidth,
  stickyLeft,
} from 'pages/AssetManagement/modules/inventoryColumns';
import {useInstrumentRowActions} from 'pages/AssetManagement/assetClasses/assetInstruments/useInstrumentRowActions';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import styles from 'pages/exchanges/exchanges.module.scss';

const STATUS_CHIP = {
  ENABLED: v2.chipOk,
  PENDING: v2.chipWarn,
  DISABLED: v2.chipStop,
};

const sentence = value =>
  value ? value.charAt(0) + value.slice(1).toLowerCase() : null;

/**
 * Multi Asset → Instruments.
 *
 * The day-to-day workspace: every instrument, one tab per concrete type, seen
 * through the shared exchange lens. Exchange is a filter here rather than a
 * route parent, so All markets is always one click away and no task starts
 * with choosing a venue.
 *
 * The Exchange column only exists at All-markets scope — once scoped to one
 * venue every row would repeat the same code.
 */
export const InstrumentsWorkspace = () => {
  const navigate = useNavigate();
  const scope = useExchangeScope();
  const {exchangeId, activeExchange} = scope;

  const [type, setType] = useState(INSTRUMENT_TYPES[0]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  // What is typed, and what the list is actually filtered by. They diverge for
  // as long as the debounce is pending.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState(undefined);

  // A keystroke per request would be one query per letter against a list this
  // size; `debounce` waits for a pause the way every other search here does.
  const applySearch = useMemo(
    () =>
      debounce(value => {
        setSearch(value?.trim() ? sanitizeInput(value) : undefined);
        // The page you were on means nothing against a different result set.
        setPage(1);
      }),
    [],
  );

  const commitSearch = value => {
    applySearch(value);
    setSearch(value?.trim() ? sanitizeInput(value) : undefined);
    setPage(1);
  };

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  // A venue change re-filters the list, so an old page number would land the
  // operator on an empty page.
  useEffect(() => {
    setPage(1);
  }, [exchangeId, type.key]);

  const {data, loading, refetch} = useQuery(GET_ASSET_CLASS_INSTRUMENTS, {
    variables: {
      input: {
        assetClassKey: type.assetClassKey,
        instrumentKey: type.instrumentKey,
        ...(exchangeId ? {exchange: exchangeId} : {}),
        ...(search ? {search} : {}),
        explore: false,
        paginate: true,
        page,
        limit,
      },
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const rows = data?.getFinancialInstruments?.data ?? [];
  const total = data?.getFinancialInstruments?.pagination?.total ?? 0;

  // The same row menu the asset-class instruments list offers. Without it this
  // workspace could only open an instrument, so every status change, movement
  // or order lookup meant finding the row again under its asset class.
  const {actionsColumn, actionModals} = useInstrumentRowActions({
    instrumentKey: type.instrumentKey,
    pageOperations,
    refetch,
    navigate,
  });

  const columns = [
    {
      title: 'Ticker',
      ...stickyLeft(type.instrumentKey, 130),
      key: 'ticker',
      render: (text, record) => (
        <span className={v2.cellTicker}>{record?.ticker || '—'}</span>
      ),
    },
    {
      title: 'Name',
      ...stickyLeft(type.instrumentKey, 240),
      key: 'name',
      render: (text, record) => (
        <div>
          <div className={v2.cellName}>{record?.name}</div>
          <div className={v2.cellSub}>
            {[
              record?.listingType === 'UNLISTED' ? 'Unlisted' : 'Listed',
              sentence(record?.marketPhase),
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
      ),
    },
    // Only meaningful across venues; scoped to one it repeats on every row.
    ...(exchangeId
      ? []
      : [
          {
            title: 'Exchange',
            width: 120,
            key: 'exchange',
            render: (text, record) =>
              record?.exchange ? (
                <Tooltip title={record.exchange.stockExchangeName}>
                  <span className={v2.cellTicker}>
                    {record.exchange.code ?? '—'}
                  </span>
                </Tooltip>
              ) : (
                <span className={v2.healthEmpty}>—</span>
              ),
          },
        ]),
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (text, record) => (
        <span
          className={`${v2.chip} ${
            STATUS_CHIP[record?.status] ?? v2.chipMute
          }`}>
          {sentence(record?.status) ?? '—'}
        </span>
      ),
    },
    {
      title: 'Risk',
      key: 'risk',
      width: 130,
      render: (text, record) =>
        record?.riskLevel ? (
          <span className={v2.cellSub}>{sentence(record.riskLevel)}</span>
        ) : (
          <span className={v2.healthEmpty}>—</span>
        ),
    },
    {
      title: 'Currency',
      key: 'currency',
      width: 110,
      render: (text, record) => (
        <span className={v2.cellTicker}>
          {record?.currency?.currencyCode || '—'}
        </span>
      ),
    },
    // The query already selected the position for bonds and funds; this screen
    // simply never rendered it, so the same instrument showed its inventory on
    // the asset-class page and not here.
    ...inventoryColumns(type.instrumentKey),
    actionsColumn,
  ];

  return (
    <div>
      <div className={shell.head}>
        <div className={shell.headTop}>
          <div>
            <h1 className={shell.title}>Instruments</h1>
            <div className={shell.sub}>
              {total.toLocaleString()} {type.label.toLowerCase()}
              {activeExchange
                ? ` on ${activeExchange.code}`
                : ' across all markets'}
            </div>
          </div>
          <div className={shell.headActions}>
            <Dropdown
              trigger="click"
              menu={{
                items: INSTRUMENT_TYPES.map(option => ({
                  key: option.key,
                  label: option.label.replace(/s$/, ''),
                  onClick: () => {
                    const route = instrumentKeysFor(option.assetClassKey).find(
                      entry => entry.key === option.instrumentKey,
                    )?.route;
                    navigate(
                      `/dashboard/asset-management/create/${route}/${option.assetClassKey}`,
                    );
                  },
                })),
              }}>
              <div>
                <Button
                  disabled={!pageOperations?.create}
                  label="Create Instrument"
                />
              </div>
            </Dropdown>
          </div>
        </div>

        <ExchangeScopeSelector {...scope} />

        <div className={shell.controls}>
          <div className={shell.segmented}>
            {INSTRUMENT_TYPES.map(option => (
              <button
                type="button"
                key={option.key}
                onClick={() => setType(option)}
                className={`${shell.segment} ${
                  type.key === option.key ? shell.segmentActive : ''
                }`}>
                {option.label}
              </button>
            ))}
          </div>
          <div className={shell.controlsRight}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search instruments"
              className={shell.searchInput}
              value={searchInput}
              onChange={event => {
                const value = event.target.value;
                setSearchInput(value);
                // Clearing takes effect at once; typing waits for the pause.
                if (value.trim()) applySearch(value);
                else commitSearch(value);
              }}
              onPressEnter={() => commitSearch(searchInput)}
            />
          </div>
        </div>
      </div>

      <div className={shell.surface}>
        <Table
          loading={loading}
          dataSource={rows}
          columns={columns}
          rowKey={record => record?.id}
          pagination={false}
          className="antd-table"
          // 860 covers ticker, name, status, risk, currency and the row menu;
          // the Exchange column only exists at all-markets scope.
          scroll={{
            x: inventoryScrollWidth(type.instrumentKey, exchangeId ? 860 : 980),
          }}
          onRow={record => ({
            onClick: () =>
              navigate(
                `/dashboard/asset-management/class/${type.assetClassKey}/instruments/${record?.id}/languages`,
                // Carry the concrete type so the update route is right, and send
                // "back" to this workspace instead of the asset-class list.
                {
                  state: {
                    instrumentKey: type.instrumentKey,
                    backTo: '/dashboard/instruments',
                  },
                },
              ),
            className: 'on-row',
          })}
          locale={{
            emptyText: (
              <div className={styles.empty}>
                {search ? (
                  <>
                    No {type.label.toLowerCase()}
                    {activeExchange ? ` on ${activeExchange.code}` : ''} match
                    &ldquo;{search}&rdquo;.
                  </>
                ) : (
                  <>
                    No {type.label.toLowerCase()}
                    {activeExchange ? ` on ${activeExchange.code}` : ''} yet.
                  </>
                )}
              </div>
            ),
          }}
        />
        <Pagination
          className="ant-table-pagination"
          current={page}
          pageSize={limit}
          total={total}
          onChange={setPage}
          onShowSizeChange={(_, size) => {
            setPage(1);
            setLimit(size);
          }}
        />
      </div>
      {actionModals}
    </div>
  );
};

export default InstrumentsWorkspace;
